// The portal's global loading / empty / error / forbidden states. Every
// screen renders its reads through these, so "no data", "not allowed" and
// "the source did not answer" never look alike — and a failure always
// shows the server's code (and correlation id) beside the catalog copy.
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { APIError } from "@/lib/api";
import { describeError } from "@/i18n/errorCopy";
import { useI18n } from "@/i18n/I18nProvider";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";

export function LoadingState({ lines = 3, label }: { lines?: number; label?: string }) {
  const { t } = useI18n();
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="sr-only">{label ?? t("common.loading")}</span>
      <Skeleton lines={lines} />
    </div>
  );
}

export function EmptyState({ title, hint, className }: { title: string; hint?: ReactNode; className?: string }) {
  return (
    <div className={cn("empty", className)} role="status">
      <p className="text-sm font-semibold text-t-2">{title}</p>
      {hint && <p className="mt-1 text-xs text-t-3">{hint}</p>}
    </div>
  );
}

function statusOf(error: unknown): number | undefined {
  return error instanceof APIError ? error.status : (error as { status?: number } | null)?.status;
}

/**
 * A failed read or command. 403 renders as "not allowed" (the server's
 * words), everything else as an error; both keep the code, and the rule a
 * refusal names (details.rule) beside it.
 */
export function ErrorState({
  error,
  onRetry,
  className,
  compact = false,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
  compact?: boolean;
}) {
  const { t, locale } = useI18n();
  if (error == null) return null;
  const e = error as { status?: number; code?: string; message?: string; details?: Record<string, string> };
  const d = describeError(e, locale);
  const status = statusOf(error);
  const forbidden = status === 403;
  const requestId = error instanceof APIError ? error.requestId : undefined;
  return (
    <div className={cn("banner items-start", forbidden ? "warn" : "danger", compact && "mb-0", className)} role="alert">
      <span aria-hidden className="pt-0.5">
        {forbidden ? "⛔" : "⚠"}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-t-1">{d.title}</span>
          {d.code && <span className="mono rounded bg-bg-3 px-1.5 py-0.5 text-2xs text-t-2">{d.code}</span>}
          {d.rule && (
            <span className="mono rounded bg-bg-3 px-1.5 py-0.5 text-2xs text-t-2" title={t("error.rule")}>
              {d.rule}
            </span>
          )}
        </div>
        {d.hint && <p className="mt-1 text-xs text-t-2">{d.hint}</p>}
        {d.fromCatalog && d.serverMessage && d.serverMessage !== d.title && (
          <p className="mt-1 text-xs text-t-3">
            {t("error.serverSaid", { message: d.serverMessage })}
          </p>
        )}
        {requestId && <p className="mono mt-1 text-2xs text-t-3">{t("error.requestId", { id: requestId })}</p>}
      </div>
      {onRetry && !forbidden && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          {t("common.reload")}
        </Button>
      )}
    </div>
  );
}

/** Wraps one query's three states around its content. */
export function QueryBlock<T>({
  query,
  empty,
  children,
  lines,
}: {
  query: { data: T | undefined; error: unknown; isLoading: boolean; refetch: () => unknown };
  /** Return an empty-state node when the data holds nothing, else null. */
  empty?: (data: T) => ReactNode | null;
  children: (data: T) => ReactNode;
  lines?: number;
}) {
  if (query.isLoading) return <LoadingState lines={lines} />;
  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (query.data === undefined) return null;
  const e = empty?.(query.data);
  if (e) return <>{e}</>;
  return <>{children(query.data)}</>;
}
